import { isPlatformServer } from '@angular/common';
import { Inject, PLATFORM_ID, NgZone, Injectable } from '@angular/core';
import { Observable, fromEventPattern, NEVER } from 'rxjs';
import { map, share, filter, mapTo } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class WindowEventsService {
  private readonly listeners: { [key in keyof WindowEventMap]?: Observable<any> };

  public constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly ngZone: NgZone,
  ) {
    this.listeners = {};
  }

  public getResize$(): Observable<number> {
    return this.listen$('resize').pipe(map((event) => (event.target as Window).innerWidth));
  }

  public getMessage$(): Observable<{ type: string; message: string }> {
    const trustedHost = location.origin;
    return this.listen$('message').pipe(
      filter((ev) => ev.origin === trustedHost && ev.data && ev.data.message && ev.data.type),
      map((ev) => ev.data),
    );
  }

  public getKeyUp$(): Observable<KeyboardEvent> {
    return this.listen$('keyup');
  }

  public getKeyDown$(): Observable<KeyboardEvent> {
    return this.listen$('keydown');
  }

  public click$(): Observable<MouseEvent> {
    return this.listen$('click');
  }

  public getClosed$(): Observable<void> {
    return this.listen$('close').pipe(mapTo(undefined));
  }

  public sendMessage(type: string, message: string, target: Window) {
    if (target) {
      target.postMessage({ type, message }, location.origin);
    }
  }

  private listen$<TEvent extends keyof WindowEventMap>(event: TEvent): Observable<WindowEventMap[TEvent]> {
    if (isPlatformServer(this.platformId)) {
      return NEVER;
    }

    let listener$ = this.listeners[event] as Observable<WindowEventMap[TEvent]>;
    if (!listener$) {
      listener$ = this.ngZone.runOutsideAngular(() =>
        fromEventPattern(
          (handler: (ev: WindowEventMap[TEvent]) => void) => window.addEventListener(event, handler),
          (handler: (ev: WindowEventMap[TEvent]) => void) => window.removeEventListener(event, handler),
        ),
      );

      listener$ = listener$.pipe(share());
      this.listeners[event] = listener$ as Observable<WindowEventMap[TEvent]>;
    }

    return listener$;
  }
}
